import React, { useEffect, useRef, lazy } from 'react';

interface DeleteConfirmModalProps {
  title: string;
  message: string;
  itemName?: string;
  isOpen: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
}

// Lazy load heavy components for code splitting
const AlertTriangle = lazy(() => import('lucide-react').then(m => ({ default: m.AlertTriangle })));
const X = lazy(() => import('lucide-react').then(m => ({ default: m.X })));
const Loader2 = lazy(() => import('lucide-react').then(m => ({ default: m.Loader2 })));

export function DeleteConfirmModal({
  title,
  message,
  itemName,
  isOpen,
  loading = false,
  onConfirm,
  onCancel,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  variant = 'danger',
}: DeleteConfirmModalProps): React.JSX.Element | null {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onCancel();
      } else if (e.key === 'Enter' && !loading && e.ctrlKey) {
        // Ctrl+Enter to confirm
        e.preventDefault();
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the cancel button when modal opens
    setTimeout(() => {
      cancelButtonRef.current?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, loading, onCancel, onConfirm]);

  if (!isOpen) return null;

  const variantClasses = variant === 'danger' 
    ? 'bg-red-600 hover:bg-red-500' 
    : 'bg-amber-600 hover:bg-amber-500';

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
    >


    
      <div 
        className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-2xl max-w-md w-full" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-gradient-to-br ${variant === 'danger' ? 'from-red-600 to-red-500' : 'from-amber-600 to-amber-500'} rounded-xl flex items-center justify-center shadow-lg`}>
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 id="delete-modal-title" className="text-xl font-semibold text-amber-50 dark:text-amber-50 text-zinc-900">{title}</h3>
              {itemName && (
                <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-1">{itemName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-white dark:hover:text-white hover:text-zinc-900 transition-colors disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p id="delete-modal-description" className="text-zinc-300 dark:text-zinc-300 text-zinc-700 mb-6">{message}</p>

        <div className="flex gap-3">
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${variantClasses} text-white px-4 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2 shadow-lg`}
            aria-label={confirmText}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                <span>Processing...</span>
              </>
            ) : (
              confirmText
            )}
          </button>
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium disabled:opacity-50"
            aria-label={cancelText}
          >
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );
}

